import { apiGet, apiGetPaginated, apiPost, apiPatch, apiDelete } from './client';
import type {
  OAuthClient,
  CreateClientFormData,
  GrantType,
  ClientType,
  TokenEndpointAuthMethod,
  PaginatedResult,
} from '@/types';

// Transform API response (camelCase) to frontend format (snake_case)
interface ApiClient {
  id: string;
  productId: string;
  productName?: string;
  productSlug?: string;
  organizationName?: string;
  name: string;
  clientId: string;
  hasClientSecret?: boolean;
  redirectUris: string[];
  allowedScopes: string[];
  allowedGrantTypes: GrantType[];
  clientType: ClientType;
  tokenEndpointAuthMethod: TokenEndpointAuthMethod;
  pkceRequired: boolean;
  backchannelLogoutUri?: string | null;
  frontchannelLogoutUri?: string | null;
  secretRotatedAt?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  clientSecret?: string;
}

function transformClient(c: ApiClient): OAuthClient {
  return {
    id: c.id,
    product_id: c.productId,
    product_name: c.productName,
    product_slug: c.productSlug,
    organization_name: c.organizationName,
    name: c.name,
    client_id: c.clientId,
    has_client_secret: c.hasClientSecret,
    redirect_uris: c.redirectUris,
    allowed_scopes: c.allowedScopes,
    allowed_grant_types: c.allowedGrantTypes,
    client_type: c.clientType,
    token_endpoint_auth_method: c.tokenEndpointAuthMethod,
    pkce_required: c.pkceRequired,
    backchannel_logout_uri: c.backchannelLogoutUri || null,
    frontchannel_logout_uri: c.frontchannelLogoutUri || null,
    secret_rotated_at: c.secretRotatedAt || null,
    is_active: c.isActive,
    created_at: c.createdAt,
    updated_at: c.updatedAt || c.createdAt,
  };
}

export const clientsApi = {
  listPage: async (
    query: { productId?: string; page?: number; pageSize?: number; search?: string } = {}
  ): Promise<PaginatedResult<OAuthClient[]>> => {
    const result = await apiGetPaginated<ApiClient[]>('/api/clients', { params: query });
    return { data: result.data.map(transformClient), pagination: result.pagination };
  },

  // List clients
  list: async (productId?: string): Promise<OAuthClient[]> => {
    return (await clientsApi.listPage({ productId, pageSize: 100 })).data;
  },

  // Get client by ID
  get: async (id: string): Promise<OAuthClient> => {
    const data = await apiGet<ApiClient>(`/api/clients/${id}`);
    return transformClient(data);
  },

  // Create client
  create: async (data: CreateClientFormData): Promise<OAuthClient & { client_secret?: string }> => {
    const result = await apiPost<ApiClient>('/api/clients', {
      productId: data.product_id,
      name: data.name,
      redirectUris: data.redirect_uris,
      allowedScopes: data.allowed_scopes,
      allowedGrantTypes: data.allowed_grant_types,
      clientType: data.client_type,
      tokenEndpointAuthMethod: data.token_endpoint_auth_method,
      pkceRequired: data.pkce_required,
      backchannelLogoutUri: data.backchannel_logout_uri || undefined,
      frontchannelLogoutUri: data.frontchannel_logout_uri || undefined,
    });
    return {
      ...transformClient(result),
      client_secret: result.clientSecret,
    };
  },

  // Update client
  update: async (id: string, data: Partial<CreateClientFormData>): Promise<OAuthClient> => {
    const result = await apiPatch<ApiClient>(`/api/clients/${id}`, {
      name: data.name,
      redirectUris: data.redirect_uris,
      allowedScopes: data.allowed_scopes,
      allowedGrantTypes: data.allowed_grant_types,
      tokenEndpointAuthMethod: data.token_endpoint_auth_method,
      pkceRequired: data.pkce_required,
      backchannelLogoutUri: data.backchannel_logout_uri,
      frontchannelLogoutUri: data.frontchannel_logout_uri,
      isActive: 'is_active' in data ? data.is_active : undefined,
    });
    return transformClient(result);
  },

  // Delete client
  delete: (id: string) => apiDelete<void>(`/api/clients/${id}`),

  // Rotate secret
  rotateSecret: async (id: string): Promise<{ client_secret: string }> => {
    const result = await apiPost<{ clientId: string; clientSecret: string }>(
      `/api/clients/${id}/rotate-secret`
    );
    return { client_secret: result.clientSecret };
  },
};
